import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, useToast, LoadingOverlay } from '../components/ui';
import { InstagramConnectionErrorModal } from '../components/InstagramConnectionErrorModal';
import { Instagram2FAHelpModal } from '../components/Instagram2FAHelpModal';
import { resolveDefaultCountry } from '../constants/countries';
import {
  updateWorkspace,
  updateCurrentUser,
  testInstagrapiConnection,
  saveInstagrapiSession,
  deleteInstagrapiSession,
  submitInstagrapiChallenge,
  resendInstagrapiChallenge,
  getInstagramOAuthUrl,
  disconnectInstagramGraph,
  getWorkspace,
  getBillingSubscription,
  generateCTAImage,
  uploadCTAImage,
  deleteCTAImage,
  uploadBrandIcon,
  deleteBrandIcon,
  generateBrandIcon,
  generateProfileImage,
  uploadProfileImage,
  deleteProfileImage,
  setInstagramProfilePicture,
  getJobStatus,
} from '../api';
import { pollJobStatus } from '../utils/jobPolling';
import { compressImage, formatBytes } from '../utils/imageCompression';
import { GenerateImageModal } from '../components/GenerateImageModal';
import BillingSection from '../components/billing/BillingSection';
import { AccountCard } from '../components/settings/AccountCard';
import { InstagramConnectCard } from '../components/settings/InstagramConnectCard';
import { PublishingSettingsCard } from '../components/settings/PublishingSettingsCard';
import { RealPhotosCard } from '../components/settings/RealPhotosCard';
import { BrandIconCard } from '../components/settings/BrandIconCard';
import { ProfileImageCard } from '../components/settings/ProfileImageCard';
import { CtaImageCard } from '../components/settings/CtaImageCard';
import type { User, Workspace } from '../types';
import './SettingsPage.css';

type SettingsPageProps = {
  workspace: Workspace;
  onWorkspaceUpdate: (_updated: Workspace) => void;
  user: User;
  onUserUpdate: (_updated: User) => void;
};

type TestResult = {
  valid: boolean;
  username?: string;
  error?: string;
  authStatus?: string;
};


function SettingsPage({ workspace, onWorkspaceUpdate, user, onUserUpdate }: SettingsPageProps) {
  const { toast } = useToast();

  const [accountUsername, setAccountUsername] = useState(user.username ?? '');
  const [accountEmail, setAccountEmail] = useState(user.email ?? '');
  const [accountCountry, setAccountCountry] = useState((user.country_code ?? 'US').toUpperCase());
  const [savingAccount, setSavingAccount] = useState(false);

  const [igUsername, setIgUsername] = useState(workspace.instagrapi_username ?? '');
  const [igPassword, setIgPassword] = useState('');
  const [showIgPassword, setShowIgPassword] = useState(false);
  const [igCountry, setIgCountry] = useState(
    resolveDefaultCountry(workspace.instagram_account_country, user.country_code)
  );
  const [hasSession, setHasSession] = useState(workspace.has_instagrapi_session ?? false);
  const [igTestResult, setIgTestResult] = useState<TestResult | null>(null);
  const [testingIg, setTestingIg] = useState(false);
  const [savingSession, setSavingSession] = useState(false);
  const [deletingSession, setDeletingSession] = useState(false);
  const [connectingOAuth, setConnectingOAuth] = useState(false);
  const [disconnectingOAuth, setDisconnectingOAuth] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalAuthStatus, setErrorModalAuthStatus] = useState<string>();
  const [showInstagram2FAHelp, setShowInstagram2FAHelp] = useState(false);

  // Challenge (email / SMS verification code) state
  const [challengePending, setChallengePending] = useState(false);
  const [challengeToken, setChallengeToken] = useState('');
  const [flowId, setFlowId] = useState<string | undefined>(undefined);
  const [challengeType, setChallengeType] = useState<'email' | 'sms' | 'totp'>('email');
  const [maskedContact, setMaskedContact] = useState('');
  const [submittingChallenge, setSubmittingChallenge] = useState(false);
  const [challengeError, setChallengeError] = useState<string | undefined>();

  const [workspaceName, setWorkspaceName] = useState(workspace.name ?? '');
  const [autoApprove, setAutoApprove] = useState(workspace.auto_approve ?? false);
  const [autoPublish, setAutoPublish] = useState(workspace.auto_publish ?? false);
  const [realPhotosEnabled, setRealPhotosEnabled] = useState(
    workspace.real_photos_enabled ?? false
  );
  const [realPhotosAllowEditing, setRealPhotosAllowEditing] = useState(
    workspace.real_photos_allow_editing ?? true
  );
  const [realPhotosAllowPeople, setRealPhotosAllowPeople] = useState(
    workspace.real_photos_allow_people ?? true
  );
  const [requireFirstManual, setRequireFirstManual] = useState(
    workspace.require_first_manual_approval ?? true
  );
  const [schedulerEnabled, setSchedulerEnabled] = useState(workspace.scheduler_enabled ?? true);
  const [daysBeforeItem, setDaysBeforeItem] = useState(String(workspace.days_before_item ?? 3));
  const [daysBeforeMedia, setDaysBeforeMedia] = useState(String(workspace.days_before_media ?? 2));

  // CTA image (profile sign-off) state
  const [ctaIncludeByDefault, setCtaIncludeByDefault] = useState(
    workspace.cta_image_include_by_default ?? false
  );
  const [ctaImageUrl, setCtaImageUrl] = useState(workspace.cta_image_url ?? '');
  const [ctaPrompt, setCtaPrompt] = useState(workspace.cta_image_prompt ?? '');
  const [ctaModalOpen, setCtaModalOpen] = useState(false);
  const [ctaGenerating, setCtaGenerating] = useState(false);
  const [ctaUploading, setCtaUploading] = useState(false);
  const [ctaDeleting, setCtaDeleting] = useState(false);
  const ctaFileInputRef = useRef<HTMLInputElement>(null);
  const ctaPollCleanupRef = useRef<(() => void) | null>(null);

  // Brand icon (logo) state
  const [brandIconUrl, setBrandIconUrl] = useState(workspace.brand_icon_url ?? '');
  const [brandIconPrompt, setBrandIconPrompt] = useState(workspace.brand_icon_prompt ?? '');
  const [brandIconModalOpen, setBrandIconModalOpen] = useState(false);
  const [brandIconGenerating, setBrandIconGenerating] = useState(false);
  const [brandIconUploading, setBrandIconUploading] = useState(false);
  const [brandIconDeleting, setBrandIconDeleting] = useState(false);
  const [brandIconCompressNote, setBrandIconCompressNote] = useState('');
  const brandIconFileInputRef = useRef<HTMLInputElement>(null);
  const brandIconPollCleanupRef = useRef<(() => void) | null>(null);

  // Profile image (Instagram account picture) state
  const [profileImageUrl, setProfileImageUrl] = useState(workspace.profile_image_url ?? '');
  const [profilePrompt, setProfilePrompt] = useState(workspace.profile_image_prompt ?? '');
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileGenerating, setProfileGenerating] = useState(false);
  const [profileUploading, setProfileUploading] = useState(false);
  const [profileDeleting, setProfileDeleting] = useState(false);
  const [profileSettingIg, setProfileSettingIg] = useState(false);
  const profileFileInputRef = useRef<HTMLInputElement>(null);
  const profilePollCleanupRef = useRef<(() => void) | null>(null);

  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [validationError, setValidationError] = useState('');

  const dirtyFieldsRef = useRef(new Set<string>());
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const [entitlements, setEntitlements] = useState<Record<string, boolean>>({});
  useEffect(() => {
    getBillingSubscription(workspace.id)
      .then((sub) => setEntitlements(sub.entitlements?.features ?? {}))
      .catch(() => {
        return;
      });
  }, [workspace.id]);

  const canAutoPublish = entitlements.auto_publish !== false;
  // Unlike auto_publish this defaults to *not* entitled: a plan that never
  // reported the flag cannot source photos, so failing closed is correct.
  const canUseRealPhotos =
    entitlements.real_photos === true || workspace.has_real_photos === true;

  useEffect(() => {
    setAccountUsername(user.username ?? '');
    setAccountEmail(user.email ?? '');
    setAccountCountry((user.country_code ?? 'US').toUpperCase());
  }, [user.username, user.email, user.country_code]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const igError = params.get('ig_error');
    const authStatus = params.get('auth_status');
    if (igError) {
      setErrorModalAuthStatus(authStatus ?? undefined);
      setShowErrorModal(true);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    // Returned from a successful Instagram Business Login (Graph OAuth) redirect.
    if (params.get('ig_connected') === '1') {
      window.history.replaceState({}, '', window.location.pathname);
      toast('Instagram connected.', 'success');
      // Pull the freshly stored token/handle onto the workspace in state.
      getWorkspace(workspace.id)
        .then((fresh) => onWorkspaceUpdate(fresh))
        .catch(() => {
          /* non-fatal: badge will refresh on next load */
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dirtyFieldsRef.current.clear();
    clearTimeout(savedTimeoutRef.current);
    setIgUsername(workspace.instagrapi_username ?? '');
    setIgCountry(resolveDefaultCountry(workspace.instagram_account_country, accountCountry));
    setHasSession(workspace.has_instagrapi_session ?? false);
    setWorkspaceName(workspace.name ?? '');
    setAutoApprove(workspace.auto_approve ?? false);
    setAutoPublish(workspace.auto_publish ?? false);
    setRealPhotosEnabled(workspace.real_photos_enabled ?? false);
    setRealPhotosAllowEditing(workspace.real_photos_allow_editing ?? true);
    setRealPhotosAllowPeople(workspace.real_photos_allow_people ?? true);
    setRequireFirstManual(workspace.require_first_manual_approval ?? true);
    setSchedulerEnabled(workspace.scheduler_enabled ?? true);
    setDaysBeforeItem(String(workspace.days_before_item ?? 3));
    setDaysBeforeMedia(String(workspace.days_before_media ?? 2));
    setCtaIncludeByDefault(workspace.cta_image_include_by_default ?? false);
    setCtaImageUrl(workspace.cta_image_url ?? '');
    setCtaPrompt(workspace.cta_image_prompt ?? '');
    setBrandIconUrl(workspace.brand_icon_url ?? '');
    setBrandIconPrompt(workspace.brand_icon_prompt ?? '');
    setProfileImageUrl(workspace.profile_image_url ?? '');
    setProfilePrompt(workspace.profile_image_prompt ?? '');
  }, [workspace, accountCountry]);

  const performAutoSave = useCallback(
    async (fields: {
      workspaceName: string;
      igUsername: string;
      igCountry: string;
      autoApprove: boolean;
      autoPublish: boolean;
      requireFirstManual: boolean;
      schedulerEnabled: boolean;
      daysBeforeItem: string;
      daysBeforeMedia: string;
      ctaIncludeByDefault: boolean;
      realPhotosEnabled: boolean;
      realPhotosAllowEditing: boolean;
      realPhotosAllowPeople: boolean;
    }) => {
      const item = parseInt(fields.daysBeforeItem, 10);
      const media = parseInt(fields.daysBeforeMedia, 10);

      if (isNaN(item) || item < 1 || isNaN(media) || media < 1 || item <= media) {
        return;
      }

      setSaveStatus('saving');
      try {
        const updated = await updateWorkspace(workspace.id, {
          name: fields.workspaceName,
          instagrapi_username: fields.igUsername,
          instagram_account_country: fields.igCountry,
          auto_approve: fields.autoApprove,
          auto_publish: fields.autoPublish,
          require_first_manual_approval: fields.requireFirstManual,
          scheduler_enabled: fields.schedulerEnabled,
          days_before_item: item,
          days_before_media: media,
          cta_image_include_by_default: fields.ctaIncludeByDefault,
          real_photos_enabled: fields.realPhotosEnabled,
          real_photos_allow_editing: fields.realPhotosAllowEditing,
          real_photos_allow_people: fields.realPhotosAllowPeople,
        });
        onWorkspaceUpdate(updated);
        dirtyFieldsRef.current.clear();
        setSaveStatus('saved');
        clearTimeout(savedTimeoutRef.current);
        savedTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 2500);
      } catch (err: any) {
        setSaveStatus('error');
        toast(err?.message ?? 'Failed to save settings.', 'error');
      }
    },
    [workspace.id, onWorkspaceUpdate, toast]
  );

  useEffect(() => {
    if (dirtyFieldsRef.current.size === 0) return;
    const snapshot = {
      workspaceName,
      igUsername,
      igCountry,
      autoApprove,
      autoPublish,
      requireFirstManual,
      schedulerEnabled,
      daysBeforeItem,
      daysBeforeMedia,
      ctaIncludeByDefault,
      realPhotosEnabled,
      realPhotosAllowEditing,
      realPhotosAllowPeople,
    };
    const timer = setTimeout(() => performAutoSave(snapshot), 700);
    return () => clearTimeout(timer);
  }, [
    workspaceName,
    igUsername,
    igCountry,
    autoApprove,
    autoPublish,
    requireFirstManual,
    schedulerEnabled,
    daysBeforeItem,
    daysBeforeMedia,
    ctaIncludeByDefault,
    realPhotosEnabled,
    realPhotosAllowEditing,
    realPhotosAllowPeople,
    performAutoSave,
  ]);

  useEffect(() => {
    const item = parseInt(daysBeforeItem, 10);
    const media = parseInt(daysBeforeMedia, 10);
    if (isNaN(item) || item < 1) {
      setValidationError('Days before item must be a positive number.');
    } else if (isNaN(media) || media < 1) {
      setValidationError('Days before media must be a positive number.');
    } else if (item <= media) {
      setValidationError('"Days before item" must be greater than "Days before media".');
    } else {
      setValidationError('');
    }
  }, [daysBeforeItem, daysBeforeMedia]);

  const markDirty = (field: string) => {
    dirtyFieldsRef.current.add(field);
  };

  const handleSaveAccount = async () => {
    setSavingAccount(true);
    try {
      const result = await updateCurrentUser({
        username: accountUsername.trim() || undefined,
        email: accountEmail.trim() || undefined,
      });
      onUserUpdate(result.user);
      toast('Account updated successfully.', 'success');
    } catch (err: any) {
      toast(err?.message ?? 'Failed to update account.', 'error');
    } finally {
      setSavingAccount(false);
    }
  };

  const handleTestInstagrapi = async () => {
    const normalizedUsername = igUsername.trim();
    const normalizedCountry = igCountry.trim().toUpperCase() || 'US';

    if (!normalizedUsername || !igPassword) {
      setIgTestResult({
        valid: false,
        error: 'Please enter username and password to continue.',
      });
      return;
    }

    setTestingIg(true);
    setIgTestResult(null);
    setSavingSession(true);
    try {
      const result = await testInstagrapiConnection(
        workspace.id,
        normalizedUsername,
        igPassword,
        normalizedCountry
      );
      // Carried into the save call so the server-side connection log stitches
      // login → verification → save into one journey.
      setFlowId(result.flow_id);

      // Instagram requires email/SMS verification — show inline OTP UI
      if (result.auth_status === 'challenge_pending' && result.challenge_token) {
        setChallengeToken(result.challenge_token);
        setChallengeType((result.challenge_type as 'email' | 'sms' | 'totp') ?? 'email');
        setMaskedContact(result.masked_contact ?? '');
        setChallengeError(undefined);
        setChallengePending(true);
        return;
      }

      if (!result.valid) {
        setErrorModalAuthStatus(result.auth_status);
        setShowErrorModal(true);
        setIgTestResult({
          valid: false,
          error: result.error ?? 'Instagram login failed.',
          authStatus: result.auth_status,
        });
        return;
      }

      if (!result.session_data) {
        setIgTestResult({
          valid: false,
          error: 'Login succeeded, but no session could be created. Please try again.',
        });
        return;
      }

      await saveInstagrapiSession(
        workspace.id,
        normalizedUsername,
        result.session_data,
        normalizedCountry,
        flowId
      );
      setHasSession(true);
      setIgPassword('');
      setShowIgPassword(false);
      setIgTestResult({
        valid: true,
        username: result.username ?? normalizedUsername,
      });
      onWorkspaceUpdate({
        ...workspace,
        instagram_auth_method: 'instagrapi',
        instagrapi_username: normalizedUsername,
        instagram_account_country: normalizedCountry,
        has_instagrapi_session: true,
        instagram_needs_attention: false,
        instagram_needs_attention_reason: '',
      });
      toast(
        'Instagram connected and session saved. Conistra can now publish for this account.',
        'success'
      );
    } catch (err: any) {
      setIgTestResult({
        valid: false,
        error: err?.message ?? 'Failed to connect Instagram.',
      });
      setErrorModalAuthStatus(undefined);
      setShowErrorModal(true);
    } finally {
      setTestingIg(false);
      setSavingSession(false);
    }
  };

  const handleSubmitChallenge = async (code: string) => {
    const normalizedUsername = igUsername.trim();
    const normalizedCountry = igCountry.trim().toUpperCase() || 'US';

    setSubmittingChallenge(true);
    setChallengeError(undefined);
    try {
      const result = await submitInstagrapiChallenge(workspace.id, challengeToken, code);

      if (!result.valid) {
        setChallengeError(result.error ?? 'Verification failed. Please start over.');
        const retryableChallengeStatuses = new Set([
          'wrong_code',
          'two_factor_required',
          'two_factor_failed',
          'unknown_error',
          undefined,
        ]);
        if (!retryableChallengeStatuses.has(result.auth_status)) {
          setChallengePending(false);
          setErrorModalAuthStatus(result.auth_status);
          setShowErrorModal(true);
        }
        return;
      }

      if (!result.session_data) {
        setChallengeError('Login succeeded but no session was created. Please start over.');
        return;
      }

      await saveInstagrapiSession(
        workspace.id,
        normalizedUsername,
        result.session_data,
        normalizedCountry,
        flowId
      );

      setChallengePending(false);
      setHasSession(true);
      setIgPassword('');
      setShowIgPassword(false);
      setIgTestResult({ valid: true, username: result.username ?? normalizedUsername });
      onWorkspaceUpdate({
        ...workspace,
        instagram_auth_method: 'instagrapi',
        instagrapi_username: normalizedUsername,
        instagram_account_country: normalizedCountry,
        has_instagrapi_session: true,
        instagram_needs_attention: false,
        instagram_needs_attention_reason: '',
      });
      toast(
        'Instagram connected and session saved. Conistra can now publish for this account.',
        'success'
      );
    } catch (err: any) {
      setChallengeError(err?.message ?? 'Verification failed. Please start over.');
    } finally {
      setSubmittingChallenge(false);
    }
  };

  const handleCancelChallenge = () => {
    setChallengePending(false);
    setChallengeToken('');
    setChallengeError(undefined);
  };

  const handleResendChallenge = async () => {
    if (!challengeToken) return { sent: false, error: 'No active challenge.' };
    try {
      const res = await resendInstagrapiChallenge(workspace.id, challengeToken);
      // If the cache window expired server-side, close the challenge UI.
      if (!res.sent && res.auth_status === 'challenge_expired') {
        setChallengePending(false);
        setChallengeToken('');
      }
      return { sent: res.sent, error: res.error };
    } catch (err: any) {
      return { sent: false, error: err?.message ?? 'Could not resend the code.' };
    }
  };

  const handleDeleteSession = async () => {
    if (
      !window.confirm(
        'Delete the stored Instagram session? You will need to set it up again to publish via Instagrapi.'
      )
    ) {
      return;
    }
    setDeletingSession(true);
    try {
      await deleteInstagrapiSession(workspace.id);
      setHasSession(false);
      setIgUsername('');
      setIgPassword('');
      setShowIgPassword(false);
      setIgTestResult(null);
      onWorkspaceUpdate({
        ...workspace,
        instagram_auth_method: 'instagrapi',
        instagrapi_username: '',
        has_instagrapi_session: false,
      });
      toast('Session deleted.', 'success');
    } catch (err: any) {
      toast(err?.message ?? 'Failed to delete session.', 'error');
    } finally {
      setDeletingSession(false);
    }
  };

  // ── Instagram Business Login (official Graph OAuth) — admin/beta ─────────────

  const handleConnectInstagramOAuth = async () => {
    setConnectingOAuth(true);
    try {
      const { url } = await getInstagramOAuthUrl(workspace.id);
      // Full-page redirect to Instagram's authorization screen. It redirects
      // back to /settings?ig_connected=1 (or ?ig_error=...) when done.
      window.location.href = url;
    } catch (err: any) {
      toast(err?.message ?? 'Could not start Instagram login. Please try again.', 'error');
      setConnectingOAuth(false);
    }
  };

  const handleDisconnectInstagramOAuth = async () => {
    if (!window.confirm('Disconnect the official Instagram login for this account?')) {
      return;
    }
    setDisconnectingOAuth(true);
    try {
      await disconnectInstagramGraph(workspace.id);
      onWorkspaceUpdate({
        ...workspace,
        has_instagram_token: false,
        instagram_ig_user_id: '',
      });
      toast('Instagram disconnected.', 'success');
    } catch (err: any) {
      toast(err?.message ?? 'Failed to disconnect Instagram.', 'error');
    } finally {
      setDisconnectingOAuth(false);
    }
  };

  // ── CTA image (profile sign-off) ───────────────────────────────────────────

  useEffect(
    () => () => {
      ctaPollCleanupRef.current?.();
      ctaPollCleanupRef.current = null;
    },
    []
  );

  const handleCtaToggleDefault = (value: boolean) => {
    setCtaIncludeByDefault(value);
    markDirty('ctaIncludeByDefault');
  };

  const handleCtaUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast('Please upload an image file (JPEG, PNG, or WebP).', 'error');
      return;
    }
    setCtaUploading(true);
    try {
      const compressed = await compressImage(file, { maxDimension: 1536 });
      const result = await uploadCTAImage(workspace.id, compressed);
      setCtaImageUrl(result.cta_image_url);
      setCtaPrompt('');
      onWorkspaceUpdate({
        ...workspace,
        cta_image_url: result.cta_image_url,
        cta_image_prompt: '',
      });
      toast('CTA image uploaded.', 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to upload CTA image.', 'error');
    } finally {
      setCtaUploading(false);
    }
  };

  const handleCtaFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleCtaUpload(file);
    e.target.value = '';
  };

  const handleCtaGenerate = async (prompt: string) => {
    setCtaGenerating(true);
    try {
      const job = await generateCTAImage(workspace.id, prompt);
      ctaPollCleanupRef.current = pollJobStatus(
        job.job_id,
        getJobStatus,
        (jobResult) => {
          ctaPollCleanupRef.current = null;
          setCtaGenerating(false);
          setCtaModalOpen(false);
          const output = (jobResult.output ?? {}) as {
            data?: { cta_image_url?: string; prompt?: string };
          };
          const nextUrl = output.data?.cta_image_url ?? '';
          const nextPrompt = output.data?.prompt ?? prompt;
          if (nextUrl) {
            setCtaImageUrl(nextUrl);
            setCtaPrompt(nextPrompt);
            onWorkspaceUpdate({
              ...workspace,
              cta_image_url: nextUrl,
              cta_image_prompt: nextPrompt,
            });
            toast('CTA image generated.', 'success');
          } else {
            toast('Image generation finished without an image. Please try again.', 'error');
          }
        },
        (errorMessage) => {
          ctaPollCleanupRef.current = null;
          setCtaGenerating(false);
          toast(errorMessage, 'error');
        }
      );
    } catch (err) {
      setCtaGenerating(false);
      toast(err instanceof Error ? err.message : 'Failed to start image generation.', 'error');
    }
  };

  // ── Brand icon (logo) ─────────────────────────────────────────────────────

  useEffect(
    () => () => {
      brandIconPollCleanupRef.current?.();
      brandIconPollCleanupRef.current = null;
      profilePollCleanupRef.current?.();
      profilePollCleanupRef.current = null;
    },
    []
  );

  const handleBrandIconGenerate = async (prompt: string) => {
    setBrandIconGenerating(true);
    try {
      const job = await generateBrandIcon(workspace.id, prompt);
      brandIconPollCleanupRef.current = pollJobStatus(
        job.job_id,
        getJobStatus,
        (jobResult) => {
          brandIconPollCleanupRef.current = null;
          setBrandIconGenerating(false);
          setBrandIconModalOpen(false);
          const output = (jobResult.output ?? {}) as {
            data?: { brand_icon_url?: string; prompt?: string };
          };
          const nextUrl = output.data?.brand_icon_url ?? '';
          const nextPrompt = output.data?.prompt ?? prompt;
          if (nextUrl) {
            setBrandIconUrl(nextUrl);
            setBrandIconPrompt(nextPrompt);
            setBrandIconCompressNote('');
            onWorkspaceUpdate({
              ...workspace,
              brand_icon_url: nextUrl,
              brand_icon_prompt: nextPrompt,
            });
            toast('Brand icon generated.', 'success');
          } else {
            toast('Image generation finished without an image. Please try again.', 'error');
          }
        },
        (errorMessage) => {
          brandIconPollCleanupRef.current = null;
          setBrandIconGenerating(false);
          toast(errorMessage, 'error');
        }
      );
    } catch (err) {
      setBrandIconGenerating(false);
      toast(err instanceof Error ? err.message : 'Failed to start image generation.', 'error');
    }
  };

  const handleBrandIconUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast('Please upload an image file (JPEG, PNG, or WebP).', 'error');
      return;
    }
    setBrandIconUploading(true);
    setBrandIconCompressNote('');
    try {
      const compressed = await compressImage(file, { maxDimension: 512 });
      if (compressed.size < file.size) {
        setBrandIconCompressNote(
          `Compressed ${formatBytes(file.size)} → ${formatBytes(compressed.size)} before upload.`
        );
      }
      const result = await uploadBrandIcon(workspace.id, compressed);
      setBrandIconUrl(result.brand_icon_url);
      setBrandIconPrompt('');
      onWorkspaceUpdate({
        ...workspace,
        brand_icon_url: result.brand_icon_url,
        brand_icon_prompt: '',
      });
      toast('Brand icon uploaded.', 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to upload brand icon.', 'error');
    } finally {
      setBrandIconUploading(false);
    }
  };

  const handleBrandIconFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleBrandIconUpload(file);
    e.target.value = '';
  };

  const handleBrandIconDelete = async () => {
    if (!window.confirm('Remove the brand icon? Your image agent will no longer reference it.')) {
      return;
    }
    setBrandIconDeleting(true);
    try {
      await deleteBrandIcon(workspace.id);
      setBrandIconUrl('');
      setBrandIconPrompt('');
      setBrandIconCompressNote('');
      onWorkspaceUpdate({ ...workspace, brand_icon_url: '', brand_icon_prompt: '' });
      toast('Brand icon removed.', 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to remove brand icon.', 'error');
    } finally {
      setBrandIconDeleting(false);
    }
  };

  const handleCtaDelete = async () => {
    if (
      !window.confirm('Remove the CTA image? Posts will no longer include it as the last slide.')
    ) {
      return;
    }
    setCtaDeleting(true);
    try {
      await deleteCTAImage(workspace.id);
      setCtaImageUrl('');
      setCtaPrompt('');
      onWorkspaceUpdate({
        ...workspace,
        cta_image_url: '',
        cta_image_prompt: '',
      });
      toast('CTA image removed.', 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to remove CTA image.', 'error');
    } finally {
      setCtaDeleting(false);
    }
  };

  // ── Profile image (Instagram account picture) ──────────────────────────────

  const handleProfileGenerate = async (prompt: string) => {
    setProfileGenerating(true);
    try {
      const job = await generateProfileImage(workspace.id, prompt);
      profilePollCleanupRef.current = pollJobStatus(
        job.job_id,
        getJobStatus,
        (jobResult) => {
          profilePollCleanupRef.current = null;
          setProfileGenerating(false);
          setProfileModalOpen(false);
          const output = (jobResult.output ?? {}) as {
            data?: { profile_image_url?: string; prompt?: string };
          };
          const nextUrl = output.data?.profile_image_url ?? '';
          const nextPrompt = output.data?.prompt ?? prompt;
          if (nextUrl) {
            setProfileImageUrl(nextUrl);
            setProfilePrompt(nextPrompt);
            onWorkspaceUpdate({
              ...workspace,
              profile_image_url: nextUrl,
              profile_image_prompt: nextPrompt,
            });
            toast('Profile image generated.', 'success');
          } else {
            toast('Image generation finished without an image. Please try again.', 'error');
          }
        },
        (errorMessage) => {
          profilePollCleanupRef.current = null;
          setProfileGenerating(false);
          toast(errorMessage, 'error');
        }
      );
    } catch (err) {
      setProfileGenerating(false);
      toast(err instanceof Error ? err.message : 'Failed to start image generation.', 'error');
    }
  };

  const handleProfileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast('Please upload an image file (JPEG, PNG, or WebP).', 'error');
      return;
    }
    setProfileUploading(true);
    try {
      const compressed = await compressImage(file, { maxDimension: 1080 });
      const result = await uploadProfileImage(workspace.id, compressed);
      setProfileImageUrl(result.profile_image_url);
      setProfilePrompt('');
      onWorkspaceUpdate({
        ...workspace,
        profile_image_url: result.profile_image_url,
        profile_image_prompt: '',
      });
      toast('Profile image uploaded.', 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to upload profile image.', 'error');
    } finally {
      setProfileUploading(false);
    }
  };

  const handleProfileFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleProfileUpload(file);
    e.target.value = '';
  };

  const handleProfileDelete = async () => {
    if (!window.confirm('Remove the profile image?')) {
      return;
    }
    setProfileDeleting(true);
    try {
      await deleteProfileImage(workspace.id);
      setProfileImageUrl('');
      setProfilePrompt('');
      onWorkspaceUpdate({
        ...workspace,
        profile_image_url: '',
        profile_image_prompt: '',
      });
      toast('Profile image removed.', 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to remove profile image.', 'error');
    } finally {
      setProfileDeleting(false);
    }
  };

  const handleSetIgProfilePicture = async () => {
    if (
      !window.confirm(
        'Push this image as your Instagram profile picture? This updates your live account right now.'
      )
    ) {
      return;
    }
    setProfileSettingIg(true);
    try {
      await setInstagramProfilePicture(workspace.id);
      toast('Instagram profile picture updated.', 'success');
    } catch (err) {
      toast(
        err instanceof Error ? err.message : 'Failed to update Instagram profile picture.',
        'error'
      );
    } finally {
      setProfileSettingIg(false);
    }
  };

  const handleIgUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIgUsername(e.target.value);
    setIgTestResult(null);
    markDirty('igUsername');
  };

  const handleIgPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIgPassword(e.target.value);
    setIgTestResult(null);
  };

  const handleIgCountryChange = (code: string) => {
    setIgCountry(code.toUpperCase());
    setIgTestResult(null);
    markDirty('igCountry');
  };

  return (
    <div className="settings-page">
      {saveStatus === 'saving' && <LoadingOverlay />}

      {saveStatus !== 'idle' && (
        <div className={`settings-autosave-indicator settings-autosave-indicator--${saveStatus}`}>
          {saveStatus === 'saving' && '⏳ Saving…'}
          {saveStatus === 'saved' && '✓ Saved'}
          {saveStatus === 'error' && '⚠ Save failed'}
        </div>
      )}

      <AccountCard
        accountUsername={accountUsername}
        setAccountUsername={setAccountUsername}
        accountEmail={accountEmail}
        setAccountEmail={setAccountEmail}
        savingAccount={savingAccount}
        handleSaveAccount={handleSaveAccount}
      />

      <Card elevation="raised">
        <Card.Header>
          <Card.Title subtitle="Your current plan, usage, and upgrade options.">
            <span className="settings-section-icon">💳</span> Subscription &amp; Billing
          </Card.Title>
        </Card.Header>
        <Card.Body className="settings-card-body">
          <BillingSection workspaceId={workspace.id} />
        </Card.Body>
      </Card>

      <InstagramConnectCard
        workspace={workspace}
        setShowInstagram2FAHelp={setShowInstagram2FAHelp}
        igCountry={igCountry}
        handleIgCountryChange={handleIgCountryChange}
        igUsername={igUsername}
        handleIgUsernameChange={handleIgUsernameChange}
        hasSession={hasSession}
        showIgPassword={showIgPassword}
        igPassword={igPassword}
        handleIgPasswordChange={handleIgPasswordChange}
        setShowIgPassword={setShowIgPassword}
        deletingSession={deletingSession}
        handleDeleteSession={handleDeleteSession}
        challengePending={challengePending}
        challengeType={challengeType}
        maskedContact={maskedContact}
        submittingChallenge={submittingChallenge}
        challengeError={challengeError}
        handleSubmitChallenge={handleSubmitChallenge}
        handleCancelChallenge={handleCancelChallenge}
        handleResendChallenge={handleResendChallenge}
        testingIg={testingIg}
        savingSession={savingSession}
        igTestResult={igTestResult}
        handleTestInstagrapi={handleTestInstagrapi}
        isAdmin={!!user.is_admin}
        connectingOAuth={connectingOAuth}
        disconnectingOAuth={disconnectingOAuth}
        handleConnectInstagramOAuth={handleConnectInstagramOAuth}
        handleDisconnectInstagramOAuth={handleDisconnectInstagramOAuth}
      />

      <PublishingSettingsCard
        workspaceName={workspaceName}
        setWorkspaceName={setWorkspaceName}
        markDirty={markDirty}
        autoApprove={autoApprove}
        setAutoApprove={setAutoApprove}
        autoPublish={autoPublish}
        canAutoPublish={canAutoPublish}
        setAutoPublish={setAutoPublish}
        requireFirstManual={requireFirstManual}
        setRequireFirstManual={setRequireFirstManual}
        schedulerEnabled={schedulerEnabled}
        setSchedulerEnabled={setSchedulerEnabled}
        daysBeforeItem={daysBeforeItem}
        setDaysBeforeItem={setDaysBeforeItem}
        daysBeforeMedia={daysBeforeMedia}
        setDaysBeforeMedia={setDaysBeforeMedia}
      />

      <RealPhotosCard
        entitled={canUseRealPhotos}
        enabled={realPhotosEnabled}
        setEnabled={setRealPhotosEnabled}
        allowEditing={realPhotosAllowEditing}
        setAllowEditing={setRealPhotosAllowEditing}
        allowPeople={realPhotosAllowPeople}
        setAllowPeople={setRealPhotosAllowPeople}
        markDirty={markDirty}
      />

      <BrandIconCard
        brandIconFileInputRef={brandIconFileInputRef}
        handleBrandIconFileSelect={handleBrandIconFileSelect}
        brandIconUrl={brandIconUrl}
        brandIconPrompt={brandIconPrompt}
        brandIconCompressNote={brandIconCompressNote}
        setBrandIconModalOpen={setBrandIconModalOpen}
        brandIconGenerating={brandIconGenerating}
        brandIconUploading={brandIconUploading}
        brandIconDeleting={brandIconDeleting}
        handleBrandIconDelete={handleBrandIconDelete}
      />

      <ProfileImageCard
        profileFileInputRef={profileFileInputRef}
        handleProfileFileSelect={handleProfileFileSelect}
        profileImageUrl={profileImageUrl}
        profilePrompt={profilePrompt}
        setProfileModalOpen={setProfileModalOpen}
        profileGenerating={profileGenerating}
        profileUploading={profileUploading}
        profileDeleting={profileDeleting}
        profileSettingIg={profileSettingIg}
        handleProfileDelete={handleProfileDelete}
        hasSession={hasSession}
        handleSetIgProfilePicture={handleSetIgProfilePicture}
      />

      <CtaImageCard
        ctaFileInputRef={ctaFileInputRef}
        handleCtaFileSelect={handleCtaFileSelect}
        ctaImageUrl={ctaImageUrl}
        ctaPrompt={ctaPrompt}
        setCtaModalOpen={setCtaModalOpen}
        ctaGenerating={ctaGenerating}
        ctaUploading={ctaUploading}
        ctaDeleting={ctaDeleting}
        handleCtaDelete={handleCtaDelete}
        ctaIncludeByDefault={ctaIncludeByDefault}
        handleCtaToggleDefault={handleCtaToggleDefault}
      />

      {validationError && <p className="settings-error-text">{validationError}</p>}

      {ctaModalOpen && (
        <GenerateImageModal
          title="✨ Create your sign-off image"
          intro="We'll use your strategy and brand colors to create a closing image that fits the rest of your account. Add any extra direction below — what should it say, what feeling, any words to put on it."
          label="What should the sign-off image look like?"
          placeholder="e.g. A clean Follow for more card matching our colors, with our handle in the corner."
          suggestions={[
            'A clean "Follow for more" sign-off in our brand colors with subtle texture.',
            'A bold "Save this post" closing slide that fits a motivational fitness account.',
            'Minimal "Tag a friend who needs this" closing card with our typography.',
          ]}
          initialPrompt={ctaPrompt}
          generating={ctaGenerating}
          onClose={() => {
            if (!ctaGenerating) setCtaModalOpen(false);
          }}
          onGenerate={handleCtaGenerate}
        />
      )}

      {brandIconModalOpen && (
        <GenerateImageModal
          title="✨ Create your brand icon"
          intro="We'll use your account name, niche, and brand colors to design a clean logo mark. Add any extra direction below — a symbol, a monogram, a style."
          label="What should the brand icon look like?"
          placeholder="e.g. A minimal line-art mountain monogram in our primary color on a soft background."
          suggestions={[
            'A minimal monogram of our initials in our brand colors.',
            'A bold, flat icon symbolizing our niche, simple enough to read tiny.',
            'A modern emblem-style badge with a single clean symbol.',
          ]}
          initialPrompt={brandIconPrompt}
          generating={brandIconGenerating}
          onClose={() => {
            if (!brandIconGenerating) setBrandIconModalOpen(false);
          }}
          onGenerate={handleBrandIconGenerate}
        />
      )}

      {profileModalOpen && (
        <GenerateImageModal
          title="✨ Create your profile picture"
          intro="We'll design a bold, recognizable avatar that fits your brand and stays safe inside Instagram's circular crop. Your brand icon is folded in automatically when you have one."
          label="What should the profile picture look like?"
          placeholder="e.g. Our logo centered on a gradient of our brand colors, clean and premium."
          suggestions={[
            'Our brand icon centered on a tasteful gradient of our colors.',
            'A bold monogram avatar that reads clearly at tiny sizes.',
            'A clean mascot-style face matching our brand vibe.',
          ]}
          initialPrompt={profilePrompt}
          generating={profileGenerating}
          onClose={() => {
            if (!profileGenerating) setProfileModalOpen(false);
          }}
          onGenerate={handleProfileGenerate}
        />
      )}

      {showErrorModal && (
        <InstagramConnectionErrorModal
          authStatus={errorModalAuthStatus}
          onClose={() => setShowErrorModal(false)}
          onRetry={() => {
            setShowErrorModal(false);
            setIgTestResult(null);
          }}
        />
      )}

      {showInstagram2FAHelp && (
        <Instagram2FAHelpModal onClose={() => setShowInstagram2FAHelp(false)} />
      )}
    </div>
  );
}

export default SettingsPage;
